import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { createRmaJob } from '../utils/rmaDb';
import type { DeviceCategory, RmaPriority } from '../utils/rmaDb';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Mail, 
  Smartphone, 
  DollarSign, 
  Briefcase,
  Camera,
  Upload
} from 'lucide-react';
import toast from 'react-hot-toast';

const NewRmaJob = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Images state & Camera/Lightbox control
  const [images, setImages] = useState<string[]>([]);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Start Camera Stream
  const startCamera = async () => {
    try {
      setIsCameraOpen(true);
      setTimeout(async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: 'environment' }
          });
          setCameraStream(stream);
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        } catch (e) {
          console.error('Camera stream access failed:', e);
          toast.error('Could not access camera. Please check permissions.');
          setIsCameraOpen(false);
        }
      }, 300);
    } catch (err) {
      console.error('Failed to open camera:', err);
      toast.error('Webcam initialization failed.');
      setIsCameraOpen(false);
    }
  };

  // Close Camera & Stop Streams
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  // Capture Image Snapshot
  const capturePhoto = () => {
    if (!videoRef.current) return;

    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        
        // Append to local state
        setImages(prev => [...prev, dataUrl]);
        toast.success('Camera snapshot captured!');
      }
      
      stopCamera();
    } catch (err) {
      console.error('Photo capture failed:', err);
      toast.error('Could not capture frame.');
      stopCamera();
    }
  };

  // File Upload base64 Conversion
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are supported');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setImages(prev => [...prev, reader.result as string]);
        toast.success('Photo uploaded and attached!');
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Delete Attached Image
  const handleDeleteImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    toast.success('Attached image removed');
  };

  // Form State
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    deviceCategory: 'Phone' as DeviceCategory,
    deviceBrand: '',
    deviceModel: '',
    serialNumber: '',
    reportedProblem: '',
    diagnostics: '',
    priority: 'Medium' as RmaPriority,
    assignedTo: 'Marcus Vance (Senior Tech)',
    estimatedCost: 100,
    advancePayment: 0,
    actualCost: 0,
    status: 'Intake' as const
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'estimatedCost' || name === 'advancePayment' ? Number(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Basic validations
      if (!formData.customerName || !formData.customerPhone || !formData.deviceModel || !formData.serialNumber) {
        throw new Error('Please fill in all required fields');
      }

      // Save to mock database
      const newJob = createRmaJob({
        customerName: formData.customerName,
        customerPhone: formData.customerPhone,
        customerEmail: formData.customerEmail,
        deviceCategory: formData.deviceCategory,
        deviceBrand: formData.deviceBrand,
        deviceModel: formData.deviceModel,
        serialNumber: formData.serialNumber,
        reportedProblem: formData.reportedProblem,
        diagnostics: formData.diagnostics,
        priority: formData.priority,
        assignedTo: formData.assignedTo,
        estimatedCost: formData.estimatedCost,
        advancePayment: formData.advancePayment,
        actualCost: formData.actualCost,
        status: formData.status,
        images: images
      });

      toast.success(`RMA Job ${newJob.id} registered successfully!`);
      // Redirect to the job detail page so tech can view timeline or add details
      navigate(`/rma-jobs/${newJob.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'An error occurred during intake');
    } finally {
      setIsLoading(false);
    }
  };

  const technicians = [
    'Marcus Vance (Senior Tech)',
    'Sarah Connor (Board Repair specialist)',
    'Ken Toyoda (Console Specialist)',
    'Alex Mercer (Level 2 Technician)'
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full text-slate-800">
      <div className="flex flex-1 w-full">
        <Sidebar />
        
        <main className="flex-1 p-6 md:p-8 w-full max-h-screen overflow-y-auto">
          <div className="w-full space-y-6">
            
            {/* Back to list & Title */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/rma-jobs')}
                className="p-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-500 hover:text-slate-700 transition-colors shadow-sm"
              >
                <ArrowLeft className="h-4.5 w-4.5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">New Repair Intake</h1>
                <p className="text-xs text-slate-400">Open a new ticket and capture client device conditions.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* SECTION 1: CUSTOMER DETAILS */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-50">
                  <User className="h-5 w-5 text-blue-500" />
                  <h3 className="text-base font-bold text-slate-800">Customer Contact Details</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="customerName" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Customer Name <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative rounded-xl shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <User className="h-4 w-4" />
                      </div>
                      <input
                        type="text"
                        name="customerName"
                        id="customerName"
                        required
                        value={formData.customerName}
                        onChange={handleChange}
                        placeholder="e.g. John Doe"
                        className="block w-full pl-9 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="customerPhone" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Phone Number <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative rounded-xl shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Phone className="h-4 w-4" />
                      </div>
                      <input
                        type="tel"
                        name="customerPhone"
                        id="customerPhone"
                        required
                        value={formData.customerPhone}
                        onChange={handleChange}
                        placeholder="e.g. +1 (555) 123-4567"
                        className="block w-full pl-9 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="customerEmail" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Email Address <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative rounded-xl shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Mail className="h-4 w-4" />
                      </div>
                      <input
                        type="email"
                        name="customerEmail"
                        id="customerEmail"
                        required
                        value={formData.customerEmail}
                        onChange={handleChange}
                        placeholder="e.g. john.doe@example.com"
                        className="block w-full pl-9 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 2: DEVICE DETAILS & FAULTS */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-50">
                  <Smartphone className="h-5 w-5 text-indigo-500" />
                  <h3 className="text-base font-bold text-slate-800">Device Specifications</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor="deviceCategory" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Device Type <span className="text-rose-500">*</span>
                    </label>
                    <select
                      name="deviceCategory"
                      id="deviceCategory"
                      value={formData.deviceCategory}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50"
                    >
                      <option value="Phone">Phone</option>
                      <option value="Laptop">Laptop</option>
                      <option value="Tablet">Tablet</option>
                      <option value="Console">Console</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="deviceBrand" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Brand / Manufacturer <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="deviceBrand"
                      id="deviceBrand"
                      required
                      value={formData.deviceBrand}
                      onChange={handleChange}
                      placeholder="e.g. Apple, Sony, Samsung"
                      className="block w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="deviceModel" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Model <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="deviceModel"
                      id="deviceModel"
                      required
                      value={formData.deviceModel}
                      onChange={handleChange}
                      placeholder="e.g. PlayStation 5, iPhone 14 Pro"
                      className="block w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="serialNumber" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Serial / IMEI Number <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="serialNumber"
                      id="serialNumber"
                      required
                      value={formData.serialNumber}
                      onChange={handleChange}
                      placeholder="Serial or IMEI code"
                      className="block w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <div>
                    <label htmlFor="reportedProblem" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Client Reported Problem <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      name="reportedProblem"
                      id="reportedProblem"
                      required
                      rows={3}
                      value={formData.reportedProblem}
                      onChange={handleChange}
                      placeholder="State clearly what the customer reported (e.g. Screen cracked, touch doesn't work, drops power at random times)..."
                      className="block w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                    ></textarea>
                  </div>

                  <div>
                    <label htmlFor="diagnostics" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Initial Diagnostics / Tech Comments (Optional)
                    </label>
                    <textarea
                      name="diagnostics"
                      id="diagnostics"
                      rows={2}
                      value={formData.diagnostics}
                      onChange={handleChange}
                      placeholder="Add any visible scratch marks, secondary damage, or diagnostic tests run during intake..."
                      className="block w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* SECTION 3: JOB DETAILS & ESTIMATES */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-50">
                  <Briefcase className="h-5 w-5 text-amber-500" />
                  <h3 className="text-base font-bold text-slate-800">Job Settings & Cost Approvals</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor="priority" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Priority Level
                    </label>
                    <select
                      name="priority"
                      id="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Urgent">Urgent (ASAP)</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="assignedTo" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Assign Technician
                    </label>
                    <select
                      name="assignedTo"
                      id="assignedTo"
                      value={formData.assignedTo}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50"
                    >
                      {technicians.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="estimatedCost" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Estimated Cost ($) <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative rounded-xl shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <DollarSign className="h-4 w-4" />
                      </div>
                      <input
                        type="number"
                        name="estimatedCost"
                        id="estimatedCost"
                        required
                        min={0}
                        value={formData.estimatedCost}
                        onChange={handleChange}
                        className="block w-full pl-8 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="advancePayment" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Advance Deposit Paid ($)
                    </label>
                    <div className="relative rounded-xl shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <DollarSign className="h-4 w-4" />
                      </div>
                      <input
                        type="number"
                        name="advancePayment"
                        id="advancePayment"
                        min={0}
                        value={formData.advancePayment}
                        onChange={handleChange}
                        className="block w-full pl-8 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 4: DEVICE INTAKE PHOTOS */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-50">
                  <Camera className="h-5 w-5 text-blue-500" />
                  <h3 className="text-base font-bold text-slate-800">Device Condition & Intake Photos</h3>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative group rounded-xl overflow-hidden border border-slate-150 aspect-video bg-slate-50">
                      <img 
                        src={img} 
                        alt={`Intake condition ${idx + 1}`} 
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                        onClick={() => setLightboxImage(img)}
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => setLightboxImage(img)}
                          className="px-2 py-1 bg-white/90 hover:bg-white text-slate-800 rounded-lg text-xs font-bold transition-colors"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(idx)}
                          className="px-2 py-1 bg-rose-600/90 hover:bg-rose-600 text-white rounded-lg text-xs font-bold transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="border border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center p-3 text-center hover:bg-slate-50 transition-colors aspect-video">
                    <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full text-blue-600">
                      <Upload className="h-5 w-5 mb-1 text-slate-400" />
                      <span className="text-[10px] font-bold uppercase tracking-wider">Upload File</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={startCamera}
                    className="border border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center p-3 text-center hover:bg-slate-50 text-blue-600 transition-colors aspect-video"
                  >
                    <Camera className="h-5 w-5 mb-1 text-slate-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Webcam Capture</span>
                  </button>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate('/rma-jobs')}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-semibold text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center px-5 py-2.5 rounded-xl text-white bg-blue-600 hover:bg-blue-700 font-semibold text-sm shadow-md hover:shadow-lg disabled:opacity-50 transition-all"
                >
                  {isLoading ? 'Registering Ticket...' : 'Save Intake & Create RMA'}
                </button>
              </div>

            </form>
          </div>
        </main>
      </div>

      {isCameraOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl border border-slate-800 p-6 flex flex-col items-center gap-4 text-white">
            <div className="w-full flex justify-between items-center pb-2 border-b border-slate-800">
              <h3 className="text-sm font-bold uppercase tracking-wider">Live Webcam Capture</h3>
              <button type="button" onClick={stopCamera} className="hover:text-white text-slate-400 transition-colors">✕</button>
            </div>
            <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden border border-slate-800">
              <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={stopCamera} className="px-4 py-2 border border-slate-700 rounded-xl">Cancel</button>
              <button type="button" onClick={capturePhoto} className="px-5 py-2.5 bg-blue-600 rounded-xl">Capture Snapshot</button>
            </div>
          </div>
        </div>
      )}

      {lightboxImage && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightboxImage(null)}>
          <div className="max-w-4xl max-h-[90vh]">
            <img src={lightboxImage} alt="Fullscreen" className="max-w-full max-h-[85vh] object-contain rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default NewRmaJob;
